/**
 * The league store the deployed app runs on.
 *
 * `MemoryStore` was always meant to be the offline half of a pair, and this is the other half.
 * Same interface, so no screen knows which one it is talking to; the difference is that ten people
 * on ten devices now see the same league.
 *
 * WHY THIS EXISTS AT ALL. localStorage is per-browser, which means a manager who declares his
 * slate on a laptop and then opens the app on his phone sees an empty form — and worse, the
 * commissioner reviewing slates sees only his own. For a keeper deadline where all ten submit
 * within a day of each other, that is not a rough edge, it is the whole thing not working.
 *
 * THREE DESIGN DECISIONS worth knowing before reading the code:
 *
 *   A LOCAL MIRROR, FED BY LISTENERS. `LeagueStore`'s getters return promises but the UI calls
 *   them constantly, on every repaint. Round-tripping each one to Firestore would be slow and
 *   would bill per read. So every live collection is subscribed once with `onSnapshot`, kept in
 *   the same Maps `MemoryStore` uses, and the getters read the mirror. A getter awaits only the
 *   FIRST snapshot of its collection; after that it is synchronous in all but type. This also
 *   means writes appear instantly — the SDK applies them to the local cache before the server
 *   confirms.
 *
 *   TRADES LIVE AT THE ROOT, everything else season-scoped. `tradesAll()` exists because a trade
 *   agreed in November about next year's picks is filed under next year, and the trade log has to
 *   show it anyway. Nesting trades under `seasons/{n}` would make that a collection-group query
 *   and an index to maintain; a root collection with a `season` field answers both questions with
 *   no ceremony. Eleven years of this league's trades is a small collection.
 *
 *   THE PLAYER POOLS ARE NOT SUBSCRIBED. They are the only documents here measured in hundreds of
 *   kilobytes, they change perhaps twice a season, and every client already ships a copy in the
 *   build. Subscribing would re-download them on every load to tell nine people something they
 *   already have. They are fetched once, on demand, and cached.
 *
 * OFFLINE. The Firestore SDK keeps its own cache and queues writes, which matters exactly once a
 * year: draft night, in somebody's basement, on hotel wifi. A pick entered while the connection is
 * out is applied locally and sent when it returns. If Firebase cannot be reached at all, `App`
 * falls back to `MemoryStore` and the evening still happens.
 */

import { docId } from '../domain/docId.js';
import { clean } from '../domain/clean.js';

export { clean };
import type { PlayerBank } from '../domain/playerBank.js';
import type { LeagueRoles } from './types.js';
import type { ProspectBank } from '../domain/prospectBank.js';
import type { Override } from '../domain/overrides.js';
import type { Snapshot } from './memoryStore.js';
import type { ProxyBallot } from '../domain/proxy.js';
import type {
  AdminNote, Avatar, DraftPick, DraftPreference, DraftState, FaabEntry, FarmNomination,
  FarmTargetList,
  PickList, GmId,
  InterpretationDoc,
  Docket, KeeperDeclaration, LeagueManager, LeagueSettings, LeagueStore, MockKeeperSet,
  AuditEntry, LiveTradeDoc, RuleProposal,
  StoreCollection, TeamPairing, Trade,
} from './types.js';

/* -- the slice of the Firestore SDK this file uses --------------------------------------------- */

/**
 * Structural types rather than imports from `firebase/firestore`.
 *
 * The offline single-file build must not pull the SDK into its bundle — it has no Firebase and no
 * network, and the whole point of that build is that it opens from a file. Importing types only
 * would be erased, but importing the functions would not, so this module receives them instead:
 * `App` hands over an already-initialised `Firestore` and the handful of functions needed, both
 * loaded behind a dynamic `import()` that never runs offline.
 *
 * It also makes the store testable without a network or an emulator, which is what
 * `tests/firestoreStore.test.ts` relies on.
 */
export interface DocData { [k: string]: unknown }

export interface FsDoc { id: string; data(): DocData | undefined; exists?: () => boolean }
export interface FsQuerySnap { docs: FsDoc[] }

/** Everything this store needs from `firebase/firestore`, and nothing else. */
/**
 * Narrow a watch to the documents a field equals a value on.
 *
 * The only reason this exists: a keeper slate is private until it is submitted, and Firestore
 * enforces that by refusing a listen that COULD return a document the rules would hide. A listen
 * on the whole collection now fails for everybody, because somebody in the league always has a
 * draft open. The query has to prove it only asks for what it may have.
 */
export interface FsFilter { field: string; equals: string }

export interface FirestoreApi {
  /** Subscribe to a collection, optionally narrowed. Returns unsubscribe. */
  watch(
    path: string, onNext: (snap: FsQuerySnap) => void, onError: (e: Error) => void,
    filter?: FsFilter,
  ): () => void;
  /** Read one document. */
  readDoc(path: string): Promise<DocData | null>;
  /** Read a whole collection once. */
  readAll(path: string): Promise<FsQuerySnap>;
  /** Create or replace one document. */
  writeDoc(path: string, data: DocData): Promise<void>;
  removeDoc(path: string): Promise<void>;
}

type Listener = (what: StoreCollection) => void;

const pairingKey = (season: number, team: string) => docId(`${season}|${team.trim().toUpperCase()}`);

/**
 * A Firestore document may not exceed 1 MiB. The player pool is the only thing here that could
 * approach it, and the failure mode without this check is an opaque SDK error on a button press
 * that looked like it worked.
 */
const MAX_DOC_BYTES = 1_000_000;

export interface FirestoreStatus {
  /** False until the first snapshot of every live collection has arrived. */
  ready: boolean;
  /** Set when Firestore refused a read — almost always a security rule, so it says which path. */
  error: string | null;
  /** The last write this store sent, for the screen that reports where data is going. */
  savedAt: string | null;
}

export class FirestoreStore implements LeagueStore {
  readonly kind = 'firestore' as const;

  private declarations_ = new Map<GmId, KeeperDeclaration>();
  private prefs_ = new Map<GmId, DraftPreference>();
  private proxies_ = new Map<GmId, ProxyBallot>();
  private trades_ = new Map<string, Trade>();
  private faab_ = new Map<string, FaabEntry>();
  private farm_ = new Map<string, FarmNomination>();
  private picks_ = new Map<string, DraftPick>();
  private notes_ = new Map<string, AdminNote>();
  private interps_ = new Map<string, InterpretationDoc>();
  private overrides_ = new Map<string, Override>();
  /**
   * Whatever this client has read or written of its OWN mock keepers.
   *
   * Not a live subscription, unlike every other collection here. Mock keepers are private to
   * their author, so a collection-wide listener would be asking the database for nine documents
   * it is going to refuse — a permission error on every draft-night page load. It is one document
   * with a known id, so it is read directly.
   */
  private mockKeepers_ = new Map<string, MockKeeperSet>();
  private proposals_ = new Map<string, RuleProposal>();
  private liveTrades_ = new Map<string, LiveTradeDoc>();
  private audit_ = new Map<string, AuditEntry>();
  private pairings_ = new Map<string, TeamPairing>();
  private roster_ = new Map<GmId, LeagueManager>();
  private faces_ = new Map<GmId, Avatar>();
  private settings_: LeagueSettings | null = null;
  private draft_: DraftState | null = null;
  private docket_: Docket | null = null;

  /** Fetched on demand, never subscribed. See the header. */
  private bank_: PlayerBank | null | undefined;
  private prospects_: ProspectBank | null | undefined;

  private listeners = new Set<Listener>();
  private unsubs: Array<() => void> = [];
  private first = new Map<string, Promise<void>>();

  readonly status: FirestoreStatus = { ready: false, error: null, savedAt: null };

  constructor(private api: FirestoreApi, readonly season: number) {
    this.watchAll();
  }

  /* -- listeners -------------------------------------------------------------------------- */

  private s(sub: string) {
    return `seasons/${this.season}/${sub}`;
  }

  /**
   * One listener per collection, each resolving a promise on its first snapshot.
   *
   * The promise is what makes the async getters honest. Without it, the first render after
   * sign-in reads an empty mirror and the app briefly shows a league with no data in it — which,
   * on the keeper screen, looks exactly like a slate that failed to save.
   */
  private live<T>(
    path: string, what: StoreCollection, into: Map<string, T>, key: (row: T) => string,
    filter?: FsFilter,
    /** A refusal here means "not yours to enumerate", not "the database is broken". */
    optional = false,
  ) {
    let settle = () => {};
    this.first.set(what, new Promise<void>((res) => { settle = res; }));
    this.unsubs.push(this.api.watch(path, (snap) => {
      into.clear();
      for (const d of snap.docs) {
        const row = d.data() as T | undefined;
        if (row) into.set(key(row), row);
      }
      settle();
      this.emit(what);
    }, (e) => {
      /*
       * A REFUSED COLLECTION IS NOT ALWAYS A FAULT (Tim, 9 September: "what's up with this
       * error?"). Some collections are readable in full only by a commissioner — `proxies` is one,
       * because a ballot nobody has lodged is its author's business. For everybody else the right
       * outcome is an empty list and a page that works, not a banner on Admin, Home and every
       * other screen saying the database refused a read.
       *
       * `optional` marks those. Anything not marked still raises, because a refusal there IS the
       * fault it looks like.
       */
      if (optional) { settle(); this.emit(what); return; }
      this.status.error = `Could not read ${path}: ${e.message}`;
      settle();
      this.emit(what);
    }, filter));
  }

  private watchAll() {
    /*
     * SUBMITTED ONLY, and this listener is the privacy boundary rather than a filter for
     * convenience. A slate a manager is still deciding on is his own business until he sends it;
     * `firestore.rules` refuses to serve anybody else's draft, so a listen on the whole collection
     * would fail outright the moment one manager had one open. His own draft comes back through
     * `declarationFor`, which reads the single document he is allowed to read.
     */
    this.live(this.s('declarations'), 'declarations', this.declarations_,
      (d: KeeperDeclaration) => d.gm, { field: 'status', equals: 'submitted' });
    this.live(this.s('preferences'), 'preferences', this.prefs_, (p: DraftPreference) => p.gm);
    /*
     * Commissioner-only to ENUMERATE — `firestore.rules` explains why. A manager gets an empty map
     * here and his own ballot through `proxyFor`, which reads the one document he may read.
     */
    this.live(this.s('proxies'), 'proxies', this.proxies_, (b: ProxyBallot) => b.gm,
      undefined, true);
    this.live(this.s('faab'), 'faab', this.faab_, (e: FaabEntry) => e.id);
    this.live(this.s('farm'), 'farmNominations', this.farm_, (n: FarmNomination) => `${n.round}.${n.slot}`);
    this.live(this.s('picks'), 'picks', this.picks_, (p: DraftPick) => String(p.overall));
    this.live(this.s('notes'), 'notes', this.notes_, (n: AdminNote) => n.id);
    this.live(this.s('interpretations'), 'interpretations', this.interps_,
      (i: InterpretationDoc) => i.id);
    this.live(this.s('overrides'), 'overrides', this.overrides_, (o: Override) => o.id);
    this.live(this.s('proposals'), 'proposals', this.proposals_, (p: RuleProposal) => p.id);
    this.live(this.s('liveTrades'), 'liveTrades', this.liveTrades_, (t: LiveTradeDoc) => t.id);
    /*
     * NOT SUBSCRIBED. Only an admin may read this collection, so a listener on every client would
     * be nine browsers asking the database for documents it is going to refuse — a permission
     * error on every page load. It is read once, on demand, by the one screen that shows it.
     */
    this.live('trades', 'trades', this.trades_, (t: Trade) => t.id);
    this.live('teamPairings', 'teamPairings', this.pairings_, (p: TeamPairing) => pairingKey(p.season, p.team));
    this.live('managers', 'managers', this.roster_, (m: LeagueManager) => m.id.toUpperCase());
    this.live('avatars', 'avatars', this.faces_, (a: Avatar) => a.gm.toUpperCase());

    /**
     * `meta` holds the three singletons — settings, draft state, docket — in one collection so
     * they arrive on one listener rather than three. They are read together on every screen that
     * cares about deadlines, so splitting them would only add round trips.
     */
    let settle = () => {};
    for (const what of ['settings', 'draftState', 'docket'] as const) {
      this.first.set(what, new Promise<void>((res) => {
        const prev = settle;
        settle = () => { prev(); res(); };
      }));
    }
    this.unsubs.push(this.api.watch(this.s('meta'), (snap) => {
      this.settings_ = null;
      this.draft_ = null;
      this.docket_ = null;
      for (const d of snap.docs) {
        const row = d.data();
        if (!row) continue;
        if (d.id === 'settings') this.settings_ = row as unknown as LeagueSettings;
        if (d.id === 'draftState') this.draft_ = row as unknown as DraftState;
        if (d.id === 'docket') this.docket_ = row as unknown as Docket;
      }
      settle();
      this.status.ready = true;
      this.emit('settings');
      this.emit('draftState');
      this.emit('docket');
    }, (e) => {
      this.status.error = `Could not read the league settings: ${e.message}`;
      settle();
      this.status.ready = true;
      this.emit('settings');
    }));
  }

  /** Stop listening. Called when the store is replaced — on sign-out, or a season change. */
  close(): void {
    for (const off of this.unsubs) off();
    this.unsubs = [];
    this.listeners.clear();
  }

  private emit(what: StoreCollection) {
    for (const l of this.listeners) l(what);
  }

  subscribe(_season: number, onChange: Listener) {
    this.listeners.add(onChange);
    return () => {
      this.listeners.delete(onChange);
    };
  }

  /** Await the first snapshot of one collection. Cheap after it has arrived. */
  private async ready(what: StoreCollection) {
    await this.first.get(what);
  }

  private async put(path: string, data: unknown) {
    await this.api.writeDoc(path, clean(data) as DocData);
    this.status.savedAt = new Date().toISOString();
  }

  /* -- settings ---------------------------------------------------------------------------- */

  async settings(season: number) {
    if (season !== this.season) return this.otherSeasonDoc<LeagueSettings>(`seasons/${season}/meta/settings`);
    await this.ready('settings');
    return this.settings_;
  }

  /**
   * Written with the two deadlines mirrored as epoch milliseconds.
   *
   * The domain stores deadlines as ISO strings and should keep doing so — they are read by people
   * in the console and rendered in two time zones. But security rules cannot parse a string into
   * a time, so a rule that refuses a late declaration has nothing to compare `request.time` to,
   * and the deadline ends up enforced only by the browser that is trying to beat it.
   *
   * `_keeperDeadlineMs` and `_preferenceDeadlineMs` exist for the rules alone. Duplicated state is
   * usually a mistake, and it is safe here for one reason: this method is the only thing in the
   * app that writes the settings document, so the copy cannot drift from the original. The
   * underscore marks them as not part of the domain type — nothing reads them but `firestore.rules`.
   */
  async saveSettings(s: LeagueSettings) {
    const ms = (iso: string | null) => {
      const t = iso ? Date.parse(iso) : NaN;
      return Number.isFinite(t) ? t : null;
    };
    await this.put(`seasons/${s.season}/meta/settings`, {
      ...s,
      _keeperDeadlineMs: ms(s.keeperDeadline),
      _preferenceDeadlineMs: ms(s.preferenceDeadline),
      /* Proposals gained a server-side gate in Pass 15; before that only the browser knew. */
      _proposalDeadlineMs: ms(s.proposalDeadline),
    });
  }

  /**
   * A season this store is not subscribed to.
   *
   * The app is overwhelmingly single-season — history comes from the archives compiled into the
   * build, not from here — so rather than spin up a second set of listeners for a stray read, one
   * document is fetched directly. Returns null on any failure, because a missing past season is
   * an absence rather than an error.
   */
  private async otherSeasonDoc<T>(path: string): Promise<T | null> {
    try {
      return (await this.api.readDoc(path)) as T | null;
    } catch {
      return null;
    }
  }

  /* -- declarations ------------------------------------------------------------------------ */

  async declarations(season: number) {
    if (season !== this.season) return this.otherSeasonAll<KeeperDeclaration>(`seasons/${season}/declarations`);
    await this.ready('declarations');
    return [...this.declarations_.values()];
  }

  /**
   * One manager's slate, submitted or not.
   *
   * Reads the document rather than the watched map, because the map holds only submitted slates —
   * see `watchAll`. This is the one read in the app that is allowed to see a draft, and the rules
   * allow it only for its owner and for a commissioner.
   */
  async declarationFor(season: number, gm: GmId) {
    const id = docId(gm.toUpperCase());
    try {
      const raw = await this.api.readDoc(`seasons/${season}/declarations/${id}`);
      return (raw as KeeperDeclaration | null) ?? null;
    } catch {
      /*
       * REFUSED AND ABSENT ARE THE SAME ANSWER HERE. A read of somebody else's draft is now
       * rejected by the rules rather than returning nothing, and every caller of this asks the
       * same question — "is there a slate for this man" — for which "you may not see it" and
       * "there isn't one" have identical consequences. Letting it throw would reject the
       * `Promise.all` it sits in and take Home's whole load down with it.
       */
      return null;
    }
  }

  async saveDeclaration(d: KeeperDeclaration) {
    await this.put(`seasons/${d.season}/declarations/${docId(d.gm)}`, d);
  }

  private async otherSeasonAll<T>(path: string): Promise<T[]> {
    try {
      const snap = await this.api.readAll(path);
      return snap.docs.map((d) => d.data() as T).filter(Boolean);
    } catch {
      return [];
    }
  }

  /* -- preferences ------------------------------------------------------------------------- */

  async preferences(season: number) {
    if (season !== this.season) return this.otherSeasonAll<DraftPreference>(`seasons/${season}/preferences`);
    await this.ready('preferences');
    return [...this.prefs_.values()];
  }

  async savePreference(p: DraftPreference) {
    await this.put(`seasons/${p.season}/preferences/${docId(p.gm)}`, p);
  }

  /* -- proxy ballots ------------------------------------------------------------------------
   *
   * NARROWED THE WAY DECLARATIONS ARE, and for the same reason. An instruction is private to its
   * author until it is lodged, so `firestore.rules` cannot allow a listen on the whole
   * collection — one unlodged ballot anywhere would fail the read for everybody. A GM reads his
   * own through `proxyFor`; the room reads the lodged ones.
   */

  async proxies(season: number) {
    if (season !== this.season) return this.otherSeasonAll<ProxyBallot>(`seasons/${season}/proxies`);
    await this.ready('proxies');
    return [...this.proxies_.values()];
  }

  /**
   * One ballot, read as one document.
   *
   * THIS CANNOT GO THROUGH `proxies()` ANY MORE. Enumeration is a commissioner's, so for everybody
   * else that map is empty and a man asking for his own instructions would be told he has none —
   * an answer that reads as "your ballot was lost". `firestore.rules` allows the single read: his
   * own, always, and anybody's that is lodged and standing.
   */
  async proxyFor(season: number, gm: GmId) {
    const who = gm.toUpperCase();
    const cached = (await this.proxies(season)).find((b) => b.gm.toUpperCase() === who);
    if (cached) return cached;
    const doc = await this.api.readDoc(`seasons/${season}/proxies/${who}`).catch(() => null);
    return (doc as ProxyBallot | null) ?? null;
  }

  async saveProxy(b: ProxyBallot) {
    await this.put(`seasons/${b.season}/proxies/${docId(b.gm)}`, b);
  }

  /* -- trades ------------------------------------------------------------------------------ */

  async trades(season: number) {
    await this.ready('trades');
    return [...this.trades_.values()]
      .filter((t) => t.season === season)
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  async tradesAll() {
    await this.ready('trades');
    return [...this.trades_.values()].sort((a, b) => b.date.localeCompare(a.date));
  }

  async saveTrade(t: Trade) {
    await this.put(`trades/${docId(t.id)}`, t);
  }

  async deleteTrade(_season: number, id: string) {
    await this.api.removeDoc(`trades/${docId(id)}`);
  }

  /* -- FAAB -------------------------------------------------------------------------------- */

  async faab(season: number) {
    if (season !== this.season) {
      return (await this.otherSeasonAll<FaabEntry>(`seasons/${season}/faab`))
        .sort((a, b) => a.date.localeCompare(b.date));
    }
    await this.ready('faab');
    return [...this.faab_.values()].sort((a, b) => a.date.localeCompare(b.date));
  }

  async saveFaab(e: FaabEntry) {
    await this.put(`seasons/${e.season}/faab/${docId(e.id)}`, e);
  }

  async deleteFaab(season: number, id: string) {
    await this.api.removeDoc(`seasons/${season}/faab/${docId(id)}`);
  }

  /* -- farm auction ------------------------------------------------------------------------ */

  async farmNominations(season: number) {
    if (season !== this.season) {
      return (await this.otherSeasonAll<FarmNomination>(`seasons/${season}/farm`))
        .sort((a, b) => a.round - b.round || a.slot - b.slot);
    }
    await this.ready('farmNominations');
    return [...this.farm_.values()].sort((a, b) => a.round - b.round || a.slot - b.slot);
  }

  async saveFarmNomination(n: FarmNomination) {
    await this.put(`seasons/${n.season}/farm/${n.round}.${n.slot}`, n);
  }

  /* -- a GM's own farm watch list ------------------------------------------------------------ */

  /**
   * NOT SUBSCRIBED, and that is the point.
   *
   * Every other collection here is a live listener over the whole path. `farmTargets` is refused
   * to everybody but its owner, so a collection listener would fail the moment a second GM had a
   * list — the same shape as declarations and proxies, and the same fix. One document, read by id,
   * and no plural method exists for a screen to reach for.
   */
  async farmTargetsFor(season: number, gm: GmId) {
    try {
      const raw = await this.api.readDoc(
        `seasons/${season}/farmTargets/${docId(gm.toUpperCase())}`,
      );
      return (raw as FarmTargetList | null) ?? null;
    } catch {
      /* Refused and absent are the same answer, as they are for a declaration. */
      return null;
    }
  }

  async saveFarmTargets(t: FarmTargetList) {
    await this.put(`seasons/${t.season}/farmTargets/${docId(t.gm)}`, t);
  }

  /**
   * A GM's picklist, one document read by id — the same shape, and for the same reason, as
   * `farmTargetsFor` above: the rules refuse everybody but the owner, so a collection listener
   * would fail the moment a second GM queued anybody.
   */
  async pickListFor(season: number, gm: GmId) {
    try {
      const raw = await this.api.readDoc(
        `seasons/${season}/pickLists/${docId(gm.toUpperCase())}`,
      );
      return (raw as PickList | null) ?? null;
    } catch {
      return null;
    }
  }

  async savePickList(l: PickList) {
    await this.put(`seasons/${l.season}/pickLists/${docId(l.gm)}`, l);
  }

  /* -- the draft --------------------------------------------------------------------------- */

  async draftState(season: number) {
    if (season !== this.season) return this.otherSeasonDoc<DraftState>(`seasons/${season}/meta/draftState`);
    await this.ready('draftState');
    return this.draft_;
  }

  async saveDraftState(s: DraftState) {
    await this.put(`seasons/${s.season}/meta/draftState`, s);
  }

  async picks(season: number) {
    if (season !== this.season) {
      return (await this.otherSeasonAll<DraftPick>(`seasons/${season}/picks`))
        .sort((a, b) => a.overall - b.overall);
    }
    await this.ready('picks');
    return [...this.picks_.values()].sort((a, b) => a.overall - b.overall);
  }

  async savePick(p: DraftPick) {
    await this.put(`seasons/${p.season}/picks/${p.overall}`, p);
  }

  /* -- notes ------------------------------------------------------------------------------- */

  async notes(season: number) {
    if (season !== this.season) {
      return (await this.otherSeasonAll<AdminNote>(`seasons/${season}/notes`))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }
    await this.ready('notes');
    return [...this.notes_.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async saveNote(n: AdminNote) {
    await this.put(`seasons/${n.season}/notes/${docId(n.id)}`, n);
  }

  /* -- interpretations --------------------------------------------------------------------- */

  async interpretations(season: number) {
    if (season !== this.season) {
      return (await this.otherSeasonAll<InterpretationDoc>(`seasons/${season}/interpretations`))
        .sort((a, b) => b.raisedAt.localeCompare(a.raisedAt));
    }
    await this.ready('interpretations');
    return [...this.interps_.values()].sort((a, b) => b.raisedAt.localeCompare(a.raisedAt));
  }

  async saveInterpretation(i: InterpretationDoc) {
    await this.put(`seasons/${i.season}/interpretations/${docId(i.id)}`, i);
  }

  /* -- overrides --------------------------------------------------------------------------- */

  async overrides(season: number) {
    if (season !== this.season) {
      return (await this.otherSeasonAll<Override>(`seasons/${season}/overrides`))
        .sort((a, b) => b.at.localeCompare(a.at));
    }
    await this.ready('overrides');
    return [...this.overrides_.values()].sort((a, b) => b.at.localeCompare(a.at));
  }

  async saveOverride(o: Override) {
    await this.put(`seasons/${o.season}/overrides/${docId(o.id)}`, o);
  }

  /* -- mock keepers, private to their author ------------------------------------------------ */

  async mockKeepers(season: number, gm: GmId) {
    const key = gm.toUpperCase();
    const doc = await this.otherSeasonDoc<MockKeeperSet>(
      `seasons/${season}/mockKeepers/${docId(key)}`,
    );
    if (doc) this.mockKeepers_.set(key, doc);
    return doc;
  }

  /**
   * EMITS BY HAND, and it is the only write in here that has to.
   *
   * Everything else in this store is repainted by a Firestore snapshot listener: you write, the
   * listener fires, the screen updates. `mockKeepers/{gm}` is deliberately NOT in the watched set
   * — it is private to one manager and read on demand rather than streamed — so there is no
   * snapshot to come back, and nothing ever told the rest of the app the guess had changed.
   *
   * What that looked like: the Guess the keepers card updated (it holds its own copy of what you
   * just clicked) while the mock board it feeds did not, because the board only reloads on a
   * `mockKeepers` event. Guesses appeared to save and then be ignored. The memory store emitted
   * correctly, so it never showed up outside the deployed app.
   */
  async saveMockKeepers(m: MockKeeperSet) {
    const key = m.gm.toUpperCase();
    this.mockKeepers_.set(key, m);
    await this.put(`seasons/${m.season}/mockKeepers/${docId(key)}`, m);
    this.emit('mockKeepers');
  }

  /* -- the player pools -------------------------------------------------------------------- */

  /**
   * Read once, cached for the life of the page.
   *
   * `undefined` on the field means "not looked at yet"; `null` means "looked, and there is none",
   * which is the normal state and the signal for the UI to use the bundled snapshot. Collapsing
   * the two would re-fetch a missing document on every repaint.
   */
  async playerBank(_season: number) {
    if (this.bank_ === undefined) {
      this.bank_ = (await this.otherSeasonDoc<PlayerBank>(this.s('banks/players'))) ?? null;
    }
    return this.bank_;
  }

  async savePlayerBank(b: PlayerBank) {
    this.guardSize(b, 'NFL player pool');
    await this.put(this.s('banks/players'), b);
    this.bank_ = b;
    this.emit('playerBank');
  }

  async prospectBank(_season: number) {
    if (this.prospects_ === undefined) {
      this.prospects_ = (await this.otherSeasonDoc<ProspectBank>(this.s('banks/prospects'))) ?? null;
    }
    return this.prospects_;
  }

  async saveProspectBank(b: ProspectBank) {
    this.guardSize(b, 'college prospect pool');
    await this.put(this.s('banks/prospects'), b);
    this.prospects_ = b;
    this.emit('prospectBank');
  }

  private guardSize(value: unknown, what: string) {
    const bytes = JSON.stringify(clean(value)).length;
    if (bytes > MAX_DOC_BYTES) {
      throw new Error(
        `The ${what} is ${Math.round(bytes / 1000)} kB and a single Firestore document holds `
        + '1 MB. It has not been saved — the build\'s own snapshot is still in use, so nothing is '
        + 'broken, but the refresh did not take.',
      );
    }
  }

  /* -- proposals and the docket ------------------------------------------------------------ */

  /* -- the audit log ------------------------------------------------------------------------ */

  async audit(season: number) {
    const rows = await this.otherSeasonAll<AuditEntry>(`seasons/${season}/audit`);
    return rows.sort((a, b) => a.at.localeCompare(b.at));
  }

  async recordAudit(e: AuditEntry) {
    /*
     * A create, never a set-with-merge: the rules refuse an update on this collection outright, so
     * a second write with the same id must not be attempted. Ids are minted with a timestamp and a
     * random suffix, so a collision is a bug rather than a retry.
     */
    await this.put(`seasons/${e.season}/audit/${docId(e.id)}`, e);
  }

  /* -- trades in flight --------------------------------------------------------------------- */

  async liveTrades(season: number) {
    if (season !== this.season) {
      return (await this.otherSeasonAll<LiveTradeDoc>(`seasons/${season}/liveTrades`))
        .sort((a, b) => a.filedAt.localeCompare(b.filedAt));
    }
    await this.ready('liveTrades');
    return [...this.liveTrades_.values()].sort((a, b) => a.filedAt.localeCompare(b.filedAt));
  }

  async saveLiveTrade(t: LiveTradeDoc) {
    await this.put(`seasons/${t.season}/liveTrades/${docId(t.id)}`, t);
  }

  async deleteLiveTrade(season: number, id: string) {
    await this.api.removeDoc(`seasons/${season}/liveTrades/${docId(id)}`);
  }

  async proposals(season: number) {
    if (season !== this.season) {
      return (await this.otherSeasonAll<RuleProposal>(`seasons/${season}/proposals`))
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }
    await this.ready('proposals');
    return [...this.proposals_.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async saveProposal(p: RuleProposal) {
    await this.put(`seasons/${p.season}/proposals/${docId(p.id)}`, p);
  }

  async deleteProposal(season: number, id: string) {
    await this.api.removeDoc(`seasons/${season}/proposals/${docId(id)}`);
  }

  async docket(season: number) {
    if (season !== this.season) return this.otherSeasonDoc<Docket>(`seasons/${season}/meta/docket`);
    await this.ready('docket');
    return this.docket_;
  }

  async saveDocket(d: Docket) {
    await this.put(`seasons/${d.season}/meta/docket`, d);
  }

  /* -- history that is not season-scoped --------------------------------------------------- */

  async teamPairings() {
    await this.ready('teamPairings');
    return [...this.pairings_.values()]
      .sort((a, b) => b.season - a.season || a.team.localeCompare(b.team));
  }

  async saveTeamPairing(p: TeamPairing) {
    await this.put(`teamPairings/${pairingKey(p.season, p.team)}`, p);
  }

  async deleteTeamPairing(season: number, team: string) {
    await this.api.removeDoc(`teamPairings/${pairingKey(season, team)}`);
  }

  async avatars() {
    await this.ready('avatars');
    return [...this.faces_.values()].sort((a, b) => a.gm.localeCompare(b.gm));
  }

  async saveAvatar(a: Avatar) {
    const gm = a.gm.toUpperCase();
    await this.put(`avatars/${docId(gm)}`, { ...a, gm });
  }

  async clearAvatar(gm: GmId) {
    await this.api.removeDoc(`avatars/${docId(gm.toUpperCase())}`);
  }

  async managers() {
    await this.ready('managers');
    return [...this.roster_.values()].sort((a, b) => a.id.localeCompare(b.id));
  }

  /**
   * Does the settings document EXIST — asked of the database, not of the cache.
   *
   * `settings()` answers from the live snapshot, which is null both when the document is absent
   * and when the subscription has simply not delivered yet. `seedCalendar` used that to decide
   * whether to write the defaults, so a slow first snapshot looked exactly like a fresh league and
   * the seed overwrote a commissioner's real deadlines with nulls. Reported 21 August: the keeper
   * deadline was set at 11:34 and gone by 11:37.
   *
   * A direct read cannot be confused that way. `putConfigIfMissing` next door already did this;
   * the settings seed was the one that did not.
   */
  async hasSettings(season: number): Promise<boolean> {
    return (await this.api.readDoc(`seasons/${season}/meta/settings`)) !== null;
  }

  async leagueConfig(): Promise<LeagueRoles | null> {
    return (await this.api.readDoc('league/config')) as unknown as LeagueRoles | null;
  }

  async saveLeagueConfig(c: LeagueRoles) {
    await this.put('league/config', c);
  }

  async saveManager(m: LeagueManager) {
    const id = m.id.toUpperCase();
    await this.put(`managers/${docId(id)}`, { ...m, id });
  }

  async deleteManager(id: GmId) {
    await this.api.removeDoc(`managers/${docId(id.toUpperCase())}`);
  }

  /* -- roles ------------------------------------------------------------------------------- */

  /**
   * Write `league/config` only if nobody has written it yet.
   *
   * This document decides who may do what — the security rules read it on every write in the
   * league — so an unconditional write from app start-up would silently undo any change an admin
   * had made to it. Read first, write only into the gap.
   *
   * Not a transaction, deliberately. Two admins signing in at the same moment would both write
   * the same roles from the same build; the race is real and its outcome is identical either way.
   */
  async putConfigIfMissing(config: { adminGms: string[]; commissionerGms: string[] }): Promise<void> {
    if (await this.api.readDoc('league/config')) return;
    await this.put('league/config', { ...config, seededAt: new Date().toISOString() });
  }

  /* -- backup ------------------------------------------------------------------------------ */

  /**
   * The same shape `MemoryStore` produces, so one backup file restores into either.
   *
   * Only the subscribed season is included, which is what "everything typed" means in practice —
   * past seasons come from the archives in the build, not from here. The banks are left out for
   * the same reason they are not subscribed: they are a copy of something already on disk.
   */
  snapshot(): Snapshot {
    return {
      version: 1,
      league: 'united_vtmd_uva_haters',
      savedAt: new Date().toISOString(),
      settings: this.settings_ ? [this.settings_] : [],
      declarations: [...this.declarations_.values()],
      preferences: [...this.prefs_.values()],
      proxies: [...this.proxies_.values()],
      trades: [...this.trades_.values()],
      liveTrades: [...this.liveTrades_.values()],
      audit: [...this.audit_.values()],
      faab: [...this.faab_.values()],
      farmNominations: [...this.farm_.values()],
      draftStates: this.draft_ ? [this.draft_] : [],
      picks: [...this.picks_.values()],
      notes: [...this.notes_.values()],
      interpretations: [...this.interps_.values()],
      overrides: [...this.overrides_.values()],
      /* Only this manager's own — no client can read anybody else's, by design. */
      mockKeepers: [...this.mockKeepers_.values()],
      proposals: [...this.proposals_.values()],
      dockets: this.docket_ ? [this.docket_] : [],
      teamPairings: [...this.pairings_.values()],
      managers: [...this.roster_.values()],
    };
  }

  /**
   * Write a whole backup into the live league.
   *
   * Deliberately an UPSERT rather than the replace `MemoryStore.load` performs. Restoring into a
   * shared database is not the same act as restoring into your own browser: dropping every
   * collection first would delete nine other people's work on the strength of one person's stale
   * file. Anything in the file overwrites its counterpart; anything not in the file is left alone.
   * The UI says which of the two it is about to do.
   */
  async restore(snap: Snapshot): Promise<void> {
    for (const s of snap.settings ?? []) await this.saveSettings(s);
    for (const d of snap.declarations ?? []) await this.saveDeclaration(d);
    for (const p of snap.preferences ?? []) await this.savePreference(p);
    for (const t of snap.trades ?? []) await this.saveTrade(t);
    for (const e of snap.faab ?? []) await this.saveFaab(e);
    for (const n of snap.farmNominations ?? []) await this.saveFarmNomination(n);
    for (const d of snap.draftStates ?? []) await this.saveDraftState(d);
    for (const p of snap.picks ?? []) await this.savePick(p);
    for (const n of snap.notes ?? []) await this.saveNote(n);
    for (const i of snap.interpretations ?? []) await this.saveInterpretation(i);
    for (const o of snap.overrides ?? []) await this.saveOverride(o);
    for (const m of snap.mockKeepers ?? []) await this.saveMockKeepers(m);
    for (const p of snap.proposals ?? []) await this.saveProposal(p);
    for (const d of snap.dockets ?? []) await this.saveDocket(d);
    for (const p of snap.teamPairings ?? []) await this.saveTeamPairing(p);
    for (const m of snap.managers ?? []) await this.saveManager(m);
  }

  /** Same reporting surface as the memory store, so one screen serves both. */
  counts(): Array<{ what: string; n: number }> {
    const s = this.snapshot();
    return [
      { what: 'manager pairings', n: s.teamPairings.length },
      { what: 'managers added or amended', n: s.managers.length },
      { what: 'trades', n: s.trades.length },
      { what: 'keeper declarations', n: s.declarations.length },
      { what: 'draft-slot preferences', n: s.preferences.length },
      { what: 'FAAB entries', n: s.faab.length },
      { what: 'picks made', n: s.picks.length },
      { what: 'farm nominations', n: s.farmNominations.length },
      { what: 'rule proposals', n: s.proposals.length },
      { what: 'commissioner notes', n: s.notes.length },
      { what: 'interpretations', n: s.interpretations?.length ?? 0 },
      { what: 'commissioner overrides', n: s.overrides?.length ?? 0 },
      { what: 'mock keeper sets', n: s.mockKeepers?.length ?? 0 },
    ].filter((x) => x.n > 0);
  }

  usage(id: GmId): Array<{ what: string; n: number }> {
    const gm = id.toUpperCase();
    const s = this.snapshot();
    const n = <T>(rows: T[], hit: (r: T) => boolean) => rows.filter(hit).length;
    return [
      { what: 'manager pairing', n: n(s.teamPairings, (p) => p.gm.toUpperCase() === gm) },
      { what: 'trade', n: n(s.trades, (t) => t.sides.some((x) => x.gm.toUpperCase() === gm)) },
      { what: 'keeper declaration', n: n(s.declarations, (d) => d.gm.toUpperCase() === gm) },
      { what: 'draft-slot preference', n: n(s.preferences, (p) => p.gm.toUpperCase() === gm) },
      { what: 'FAAB entry', n: n(s.faab, (e) => e.gm.toUpperCase() === gm) },
      { what: 'pick', n: n(s.picks, (p) => p.gm.toUpperCase() === gm) },
      { what: 'farm nomination', n: n(s.farmNominations, (f) => f.nominator.toUpperCase() === gm
        || f.winner?.toUpperCase() === gm) },
      { what: 'rule proposal', n: n(s.proposals, (p) => p.gm.toUpperCase() === gm) },
    ].filter((x) => x.n > 0);
  }
}
